import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiEndpoints } from "../../services/apiConfig";

// ✅ Upload audio files with watermark (existing)
export const uploadAudioFilesWithWaterMark = createAsyncThunk(
  "upload/uploadAudioFilesWithWaterMark",
  async ({ order_id, files, company_name }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const formData = new FormData();
      formData.append("order_id", order_id);
      formData.append("company_name", company_name);
      files.forEach((file) => formData.append("files", file));

      const response = await axios.post(
        apiEndpoints.uploadAudioWithWatermark,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Audio upload failed",
      );
    }
  },
);

// ✅ NEW: Add watermark to audio files with volume control
export const addWatermarkToAudio = createAsyncThunk(
  "upload/addWatermarkToAudio",
  async ({ files, volume = -5, order_id = null }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const formData = new FormData();

      // Append each file
      files.forEach((file) => {
        formData.append("files", file);
      });

      // Append volume
      formData.append("volume", volume);

      // Append order_id if provided
      if (order_id) {
        formData.append("order_id", order_id);
      }

      const response = await axios.post(apiEndpoints.addWatermark, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Watermark addition failed",
      );
    }
  },
);

export const downloadWatermarkedFile = createAsyncThunk(
  "upload/downloadWatermarkedFile",
  async (filename, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        apiEndpoints.downloadWatermark.replace("{filename}", filename),
        {
          responseType: "blob",
        },
      );

      return {
        blob: response.data,
        filename: filename,
        contentType: response.headers["content-type"],
      };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Download failed",
      );
    }
  },
);

export const uploadAudioFilesWithoutWaterMark = createAsyncThunk(
  "upload/uploadAudioFilesWithoutWaterMark",
  async ({ order_id, files, company_name }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const formData = new FormData();
      formData.append("order_id", order_id);
      formData.append("company_name", company_name);
      files.forEach((file) => formData.append("files", file));

      const response = await axios.post(
        apiEndpoints.uploadAudioWithoutWatermark,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Audio upload failed",
      );
    }
  },
);

export const deleteSong = createAsyncThunk(
  "delete/deleteSong",
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const response = await axios.post(apiEndpoints.deleteSong, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Audio upload failed",
      );
    }
  },
);

// ✅ Get uploaded audio by ID
export const getWatermarkUploadedAudioById = createAsyncThunk(
  "upload/getWatermarkUploadedAudioById",
  async (id, { rejectWithValue }) => {
    const token = localStorage.getItem("token");

    try {
      const response = await axios.get(
        `${apiEndpoints.getWatermarkUploadedAudiobyId}/${id}`,
      );

      return response.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        return rejectWithValue("Invalid ID — no songs found for this ID.");
      }

      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch uploaded audio",
      );
    }
  },
);

export const getfinalUploadedAudiobyId = createAsyncThunk(
  "upload/getfinalUploadedAudiobyId",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${apiEndpoints.getfinalUploadedAudiobyId}/${id}`,
      );

      return response.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        return rejectWithValue("Invalid ID — no songs found for this ID.");
      }

      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to fetch uploaded audio",
      );
    }
  },
);

// ✅ Get all songs
export const getAllWatermarkSongs = createAsyncThunk(
  "upload/getAllWatermarkSongs",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const response = await axios.get(`${apiEndpoints.getAllWatermarkSongs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to fetch songs",
      );
    }
  },
);

export const getAllWithouWatermarkSongs = createAsyncThunk(
  "upload/getAllWithouWatermarkSongs",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      const response = await axios.get(
        `${apiEndpoints.getAllWithouWatermarkSongs}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to fetch songs",
      );
    }
  },
);

export const downloadSong = createAsyncThunk(
  "download/downloadSong",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(apiEndpoints.downloadSong, payload, {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob",
      });

      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Download failed",
      );
    }
  },
);

export const approveWatermarkSong = createAsyncThunk(
  "upload/approveWatermarkSong",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        apiEndpoints.approveWatermarkSong,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Download failed",
      );
    }
  },
);

export const downloadAllWatermarkedFiles = createAsyncThunk(
  "upload/downloadAllWatermarkedFiles",
  async (filenames = [], { rejectWithValue }) => {
    try {
      if (!Array.isArray(filenames) || filenames.length === 0) {
        throw new Error("No filenames provided");
      }

      // Build query: ?files=a.mp3&files=b.mp3
      const params = new URLSearchParams();
      filenames.forEach((name) => {
        if (name) params.append("files", name);
      });

      const url = `${apiEndpoints.downloadAllWatermark}?${params.toString()}`;

      const response = await axios.get(url, {
        responseType: "blob",
      });

      return {
        blob: response.data,
        filename: "watermarked_songs.zip",
        contentType: response.headers["content-type"],
      };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Download all failed",
      );
    }
  },
);

// ✅ Slice
const uploadSlice = createSlice({
  name: "upload",
  initialState: {
    isLoading: false,
    success: false,
    error: null,
    uploadedAudio: null,
    watermarkResults: null,
    downloadLoading: false,
    downloadError: null,
    getAudioById: null,
    getFinalAudioById: null,
    getAllSongsData: null,
    getAllFinalSongsData: null,
    deleteSongResponse: null,
    downloadSongResponse: null,
    approveSongResponse: null,
    downloadAllLoading: false,
    downloadAllError: null,
  },
  reducers: {
    resetUploadState: (state) => {
      state.isLoading = false;
      state.success = false;
      state.error = null;
      state.watermarkResults = null;
      state.downloadLoading = false;
      state.downloadError = null;
    },
    resetWatermarkState: (state) => {
      state.watermarkResults = null;
      state.error = null;
      state.isLoading = false;
    },
    resetDownloadState: (state) => {
      state.downloadLoading = false;
      state.downloadError = null;
      state.downloadAllLoading = false;
      state.downloadAllError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 🟢 Upload audio with watermark (existing)
      .addCase(uploadAudioFilesWithWaterMark.pending, (state) => {
        state.isLoading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(uploadAudioFilesWithWaterMark.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.uploadedAudio = action.payload;
      })
      .addCase(uploadAudioFilesWithWaterMark.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(addWatermarkToAudio.pending, (state) => {
        state.isLoading = true;
        state.success = false;
        state.error = null;
        state.watermarkResults = null;
      })
      .addCase(addWatermarkToAudio.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.watermarkResults = action.payload;
      })
      .addCase(addWatermarkToAudio.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.watermarkResults = null;
      })

      // 🟢 NEW: Download watermarked file
      .addCase(downloadWatermarkedFile.pending, (state) => {
        state.downloadLoading = true;
        state.downloadError = null;
      })
      .addCase(downloadWatermarkedFile.fulfilled, (state, action) => {
        state.downloadLoading = false;

        // Trigger file download
        const { blob, filename, contentType } = action.payload;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .addCase(downloadWatermarkedFile.rejected, (state, action) => {
        state.downloadLoading = false;
        state.downloadError = action.payload;
      })
      .addCase(uploadAudioFilesWithoutWaterMark.pending, (state) => {
        state.isLoading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(uploadAudioFilesWithoutWaterMark.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        state.uploadedAudio = action.payload;
      })
      .addCase(uploadAudioFilesWithoutWaterMark.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // 🟣 Get uploaded audio by ID
      .addCase(getWatermarkUploadedAudioById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getWatermarkUploadedAudioById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.getAudioById = action.payload;
      })
      .addCase(getWatermarkUploadedAudioById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.getAudioById = null;
      })

      .addCase(getfinalUploadedAudiobyId.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getfinalUploadedAudiobyId.fulfilled, (state, action) => {
        state.isLoading = false;
        state.getFinalAudioById = action.payload;
      })
      .addCase(getfinalUploadedAudiobyId.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.getFinalAudioById = null;
      })

      .addCase(getAllWatermarkSongs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllWatermarkSongs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.getAllSongsData = action.payload;
      })
      .addCase(getAllWatermarkSongs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(getAllWithouWatermarkSongs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllWithouWatermarkSongs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.getAllFinalSongsData = action.payload;
      })
      .addCase(getAllWithouWatermarkSongs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(deleteSong.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSong.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deleteSongResponse = action.payload;
      })
      .addCase(deleteSong.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(downloadSong.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(downloadSong.fulfilled, (state, action) => {
        state.isLoading = false;
        state.downloadSongResponse = action.payload;
      })
      .addCase(downloadSong.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(approveWatermarkSong.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(approveWatermarkSong.fulfilled, (state, action) => {
        state.isLoading = false;
        state.approveSongResponse = action.payload;
      })
      .addCase(approveWatermarkSong.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(downloadAllWatermarkedFiles.pending, (state) => {
        state.downloadAllLoading = true;
        state.downloadAllError = null;
      })
      .addCase(downloadAllWatermarkedFiles.fulfilled, (state, action) => {
        state.downloadAllLoading = false;

        const { blob, filename } = action.payload;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "watermarked_songs.zip";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .addCase(downloadAllWatermarkedFiles.rejected, (state, action) => {
        state.downloadAllLoading = false;
        state.downloadAllError = action.payload;
      });
  },
});

export const { resetUploadState, resetWatermarkState, resetDownloadState } =
  uploadSlice.actions;
export default uploadSlice.reducer;
